// ─── What: running position and PnL per account ───────────────────────────────
//
// Pure. Lifted out of the position panel's draw block, where it was tangled with
// scale construction and path generation.
//
// Two passes, because they answer different questions. The snapshots are
// event-driven: one point per trade, carrying the cost basis forward. The PnL
// series is candle-driven: it re-marks whatever position was open at each candle
// against that candle's close, which is why an untouched position still has a
// moving PnL line.

import * as d3 from 'd3';
import type { OHLCVPoint } from '../../../../api/types';
import type { PositionSnap, TradeOverlayData } from '../../chartTypes';
import { floorOhlcv } from '../../chartData';

type Trade = TradeOverlayData['trades'][number];

/**
 * One snapshot per trade, per account, with the last one carried to `domainEnd`
 * so the step line reaches the right edge.
 *
 * `probSeriesData` is the market the unrealized leg is marked against at trade
 * time; a missing point falls back to 0.5 rather than dropping the snapshot.
 */
export function buildPositionSnapshots(
    trades: Trade[],
    accounts: Iterable<string>,
    probSeriesData: OHLCVPoint[],
    domainEnd: Date
): Map<string, PositionSnap[]> {
    const byAccount = new Map<string, PositionSnap[]>();

    for (const account of accounts) {
        const accountTrades = trades
            .filter(t => t.account === account)
            .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
        if (accountTrades.length === 0) continue;

        let yesShares = 0,
            noShares = 0;
        let yesCostBasis = 0,
            noCostBasis = 0;
        let yesBought = 0,
            noBought = 0;
        let realizedPnl = 0;
        const points: PositionSnap[] = [];

        for (const t of accountTrades) {
            const isNo = t.outcomeIndex === 1;
            if (t.side === 'buy') {
                if (isNo) {
                    noShares += t.amount;
                    noCostBasis += t.quoteAmount;
                    noBought += t.amount;
                } else {
                    yesShares += t.amount;
                    yesCostBasis += t.quoteAmount;
                    yesBought += t.amount;
                }
            } else {
                // Sells realise against average cost, so the basis has to be
                // reduced by the same average rather than by the sale proceeds.
                if (isNo) {
                    const avgCost = noBought > 0 ? noCostBasis / noBought : 0;
                    realizedPnl += t.quoteAmount - avgCost * t.amount;
                    noShares = Math.max(0, noShares - t.amount);
                    noCostBasis = Math.max(
                        0,
                        noCostBasis - avgCost * t.amount
                    );
                } else {
                    const avgCost = yesBought > 0 ? yesCostBasis / yesBought : 0;
                    realizedPnl += t.quoteAmount - avgCost * t.amount;
                    yesShares = Math.max(0, yesShares - t.amount);
                    yesCostBasis = Math.max(
                        0,
                        yesCostBasis - avgCost * t.amount
                    );
                }
            }

            const probPt =
                probSeriesData.length > 0
                    ? floorOhlcv(probSeriesData, t.timestamp)
                    : null;
            const probAtT = probPt?.open ?? 0.5;
            const unrealizedAtT =
                yesShares * probAtT +
                noShares * (1 - probAtT) -
                yesCostBasis -
                noCostBasis;

            points.push({
                t: t.timestamp,
                pos: yesShares - noShares,
                yesShares,
                noShares,
                yesCostBasis,
                noCostBasis,
                yesBought,
                noBought,
                realizedPnl,
                totalPnl: realizedPnl + unrealizedAtT,
            });
        }

        const lastSnap = points[points.length - 1];
        if (lastSnap && lastSnap.t < domainEnd)
            points.push({ ...lastSnap, t: domainEnd });
        byAccount.set(account, points);
    }

    return byAccount;
}

const stateBisect = d3.bisector<PositionSnap, Date>(d => d.t).left;

/** Re-mark each account's open position against every candle after its first trade. */
export function buildPnlPoints(
    positionsByAccount: Map<string, PositionSnap[]>,
    candleData: OHLCVPoint[]
): Map<string, PositionSnap[]> {
    const byAccount = new Map<string, PositionSnap[]>();

    for (const [account, points] of positionsByAccount) {
        const firstTradeTime = points[0]?.t;
        if (!firstTradeTime) continue;
        const pnlPoints: PositionSnap[] = [];
        for (const candle of candleData) {
            if (candle.t < firstTradeTime) continue;
            const si = stateBisect(points, candle.t, 1);
            const state = points[si - 1];
            if (!state) continue;
            const totalPnl =
                state.realizedPnl +
                state.yesShares * candle.close +
                state.noShares * (1 - candle.close) -
                state.yesCostBasis -
                state.noCostBasis;
            pnlPoints.push({ ...state, t: candle.t, totalPnl });
        }
        byAccount.set(account, pnlPoints);
    }

    return byAccount;
}

/** Signed extent across every account, always including zero. */
export function extentIncludingZero(
    seriesByAccount: Map<string, PositionSnap[]>,
    value: (p: PositionSnap) => number
): [number, number] {
    let min = 0,
        max = 0;
    for (const points of seriesByAccount.values()) {
        for (const p of points) {
            const v = value(p);
            if (v < min) min = v;
            if (v > max) max = v;
        }
    }
    return [min, max];
}
